// /stores/Booking.js
import { defineStore } from 'pinia'

import { useProductPriceAvailabilityStore } from '@/stores/productPriceAvailability'
import { useCartStore } from '@/stores/cart'
import { useProductStore } from '@/stores/product'
import { useQuerySync } from '@/composables/useQuerySync'
import { useAsyncRequest } from '@/composables/useAsyncRequest'
import { getEndDate, buildVerifyAvailabilityPayload } from '@/composables/bookingPricing'
import { normalizeArrayQueryParam } from '@/composables/queryParams'

export const useBookingStore = defineStore('booking', () => {
    const route = useRoute()
    const availabilityStore = useProductPriceAvailabilityStore()
    const productStore = useProductStore()
    const cartStore = useCartStore()
    const { request, loading: verifyLoading, error: verifyError } = useAsyncRequest()

    // --------------------------------------------
    // STATE
    // --------------------------------------------
    const form = reactive({
        productId: productStore.item?.id ?? null,
        adults: Number(route.query.adults ?? null),
        children: normalizeArrayQueryParam(route.query.children),
        startDate: route.query.start_date ?? null,
        nights: route.query.nights != null ? Number(route.query.nights) : null,
        processing: false
    })

    const verifyAvailabilityResult = ref({
        status: 0,
        item: null
    })

    // --------------------------------------------
    // COMPUTED: end date
    // --------------------------------------------
    const endDate = computed(() => {
        return getEndDate(form.startDate, form.nights)
    })

    // --------------------------------------------
    // COMPUTED: selected availability
    // --------------------------------------------
    const selectedAvailability = computed(() => {
        const verifiedItem = verifyAvailabilityResult.value?.item
        const attributes = verifiedItem?.attributes

        if (verifyAvailabilityResult.value?.status === 1 && verifiedItem && attributes) {
            const price = Number(verifiedItem?.display_price ?? verifiedItem?.price ?? 0)

            return {
                serviceType: attributes?.product_type ?? null,
                startDate: attributes?.check_in ?? form.startDate,
                endDate: attributes?.check_out ?? endDate.value,
                nights: Number(attributes?.nights ?? form.nights ?? 0),
                price: {
                    value: price,
                    final: price,
                    original: price,
                    discount: false,
                    currency: 'EUR'
                }
            }
        }

        return availabilityStore.getAvailabilityItem(form.startDate, form.nights)
    })

    // --------------------------------------------
    // COMPUTED: total price
    // --------------------------------------------
    const totalPrice = computed(() => {
        const verifiedItem = verifyAvailabilityResult.value?.item
        return Number(verifiedItem?.display_sub_total ?? verifiedItem?.sub_total ?? verifiedItem?.display_price ?? verifiedItem?.price ?? 0)
    })

    // --------------------------------------------
    // COMPUTED: capacity
    // --------------------------------------------
    const totalPersons = computed(() => form.adults + form.children.length)

    const maxOccupancy = computed(() => productStore.item?.max_occupancy ?? productStore.item?.maxOccupancy ?? Infinity)
    const maxAdultOccupancy = computed(() => productStore.item?.max_adults ?? productStore.item?.maxAdults ?? maxOccupancy.value)
    const maxChildOccupancy = computed(() => productStore.item?.max_children ?? productStore.item?.maxChildren ?? maxOccupancy.value)

    const isOverCapacity = computed(() => totalPersons.value > maxOccupancy.value)

    watch(
        [() => form.adults, () => form.children.length, maxOccupancy, maxAdultOccupancy, maxChildOccupancy],
        ([adults, childCount, totalMax, adultMax, childMax]) => {
            const normalizedAdultMax = Math.max(1, Number(adultMax) || 1)
            const normalizedChildMax = Math.max(0, Number(childMax) || 0)
            const normalizedTotalMax = Math.max(1, Number(totalMax) || 1)

            if (Number(adults) < 1) {
                form.adults = 1
                return
            }

            if (Number(adults) > normalizedAdultMax) {
                form.adults = normalizedAdultMax
                return
            }

            if (childCount > normalizedChildMax) {
                form.children = form.children.slice(0, normalizedChildMax)
                return
            }

            const overflow = Number(adults) + childCount - normalizedTotalMax
            if (overflow > 0) {
                form.children = form.children.slice(0, Math.max(0, childCount - overflow))
            }
        },
        { immediate: true }
    )

    // --------------------------------------------
    // ACTIONS
    // --------------------------------------------
    const setDates = (start) => {
        form.startDate = start
    }

    const setPax = ({ adults, children }) => {
        if (adults !== undefined) form.adults = adults
        if (children !== undefined) form.children = children

        // adjust if exceeding max occupancy
        const total = form.adults + form.children.length
        if (total > maxOccupancy.value) {
            const overflow = total - maxOccupancy.value
            form.children.splice(form.children.length - overflow)
        }
    }

    const verifyAvailability = async () => {
        const normalizedNights = Number(form.nights)

        if (!form.startDate || !form.productId || !form.adults || !Number.isFinite(normalizedNights) || normalizedNights <= 0) {
            verifyAvailabilityResult.value = {
                status: 0,
                item: null
            }
            return
        }

        const payload = buildVerifyAvailabilityPayload({
            startDate: form.startDate,
            adults: form.adults,
            nights: normalizedNights,
            children: form.children,
            productId: form.productId
        })

        try {
            const response = await request('post', '/api/products/verify-availability', payload)
            verifyAvailabilityResult.value = response?.data ?? response
        } catch {
            verifyAvailabilityResult.value = {
                status: 0,
                item: null
            }
        }
    }

    const submitBooking = () => {
        form.processing = true
        return cartStore
            .storeCart({
                ...form,
                product_id: form.productId,
                endDate: endDate.value
            })
            .finally(() => {
                form.processing = false
            })
    }

    const urlQuery = computed(() => ({
        adults: form.adults,
        children: form.children,
        start_date: form.startDate,
        nights: form.nights
    }))

    useQuerySync(urlQuery, {
        keys: ['adults', 'children', 'start_date', 'nights'],
        debounce: 300
    })

    watch(
        [() => form.startDate, () => form.nights, () => form.adults, () => form.productId, () => form.children],
        () => {
            void verifyAvailability()
        },
        { immediate: true, deep: true }
    )

    // --------------------------------------------
    // RETURN
    // --------------------------------------------
    return {
        form,
        verifyAvailabilityResult,
        endDate,
        selectedAvailability,
        totalPrice,
        totalPersons,
        maxOccupancy,
        maxAdultOccupancy,
        maxChildOccupancy,
        isOverCapacity,
        setDates,
        setPax,
        submitBooking,
        verifyAvailability,
        verifyLoading,
        verifyError
    }
})
